import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { getScannerPageHint, isScannerBusy, scannerStatuses, startBarcodeScanner } from '../cameraScanner';

const FARBEN = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#8b5cf6','#ec4899','#64748b'];

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));
}

export default function BehaelterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [behaelter, setBehaelter] = useState(null);
  const [artikel, setArtikel] = useState([]);
  const [standorte, setStandorte] = useState([]);
  const [zeigForm, setZeigForm] = useState(false);
  const [bearbeite, setBearbeite] = useState(null);
  const [scanner, setScanner] = useState({ status: scannerStatuses.idle, message: '', reason: '' });
  const [meldung, setMeldung] = useState('');
  const [meldungTyp, setMeldungTyp] = useState('success');
  const [formFehler, setFormFehler] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [form, setForm] = useState({ name: '', beschreibung: '', barcode: '', menge: '1', einheit: 'Stück', kategorie: '', notizen: '' });
  // Kiste bearbeiten
  const [zeigKisteForm, setZeigKisteForm] = useState(false);
  const [kisteForm, setKisteForm] = useState({ name: '', beschreibung: '', standort_id: '', farbe: '#6366f1' });
  const [kisteSpeichert, setKisteSpeichert] = useState(false);
  const [kisteFehler, setKisteFehler] = useState('');
  // Artikel-Foto
  const [uploadArtikelId, setUploadArtikelId] = useState(null);
  const fotoRef = useRef();
  const artikelFotoRef = useRef();
  const scannerRef = useRef(null);
  const mengeTimerRef = useRef({});
  const mengePendingRef = useRef({});

  useEffect(() => { laden(); }, [id]);
  useEffect(() => () => scannerRef.current?.stop?.(), []);

  useEffect(() => {
    if (!meldung) return;
    const t = setTimeout(() => setMeldung(''), 4000);
    return () => clearTimeout(t);
  }, [meldung]);

  async function laden() {
    const [b, a, s] = await Promise.all([
      api.get(`/behaelter/${id}`),
      api.get(`/behaelter/${id}/artikel`),
      api.get('/standorte'),
    ]);
    setBehaelter(b.data);
    setArtikel(a.data);
    setStandorte(s.data);
  }

  function zeigeMeldung(text, typ = 'success') {
    setMeldung(text);
    setMeldungTyp(typ);
  }

  // ── Kiste bearbeiten ──────────────────────────────────
  function kisteBearbeitenOeffnen() {
    setKisteForm({
      name: behaelter.name,
      beschreibung: behaelter.beschreibung || '',
      standort_id: behaelter.standort_id ? String(behaelter.standort_id) : '',
      farbe: behaelter.farbe || '#6366f1',
    });
    setKisteFehler('');
    setZeigKisteForm(true);
  }

  async function kisteSpeichern(e) {
    e.preventDefault();
    if (!kisteForm.name.trim()) { setKisteFehler('Bitte gib einen Namen ein.'); return; }
    setKisteSpeichert(true);
    setKisteFehler('');
    try {
      await api.put(`/behaelter/${id}`, kisteForm);
      setZeigKisteForm(false);
      zeigeMeldung('Kiste wurde aktualisiert.');
      laden();
    } catch {
      setKisteFehler('Kiste konnte nicht gespeichert werden. Bitte versuche es erneut.');
    } finally {
      setKisteSpeichert(false);
    }
  }

  // ── Artikel ───────────────────────────────────────────
  async function speichern(e) {
    e.preventDefault();
    if (!form.name.trim()) { setFormFehler('Bitte gib einen Artikelnamen ein.'); return; }
    setSpeichert(true);
    setFormFehler('');
    try {
      if (bearbeite) await api.put(`/artikel/${bearbeite}`, form);
      else await api.post(`/behaelter/${id}/artikel`, form);
      setZeigForm(false);
      setBearbeite(null);
      setForm({ name: '', beschreibung: '', barcode: '', menge: '1', einheit: 'Stück', kategorie: '', notizen: '' });
      zeigeMeldung(bearbeite ? 'Artikel wurde aktualisiert.' : 'Artikel wurde hinzugefügt.');
      laden();
    } catch {
      setFormFehler('Der Artikel konnte nicht gespeichert werden. Bitte prüfe die Angaben und versuche es erneut.');
    } finally {
      setSpeichert(false);
    }
  }

  async function loeschen(artId, artName) {
    if (!window.confirm(`Artikel „${artName}" wirklich löschen?`)) return;
    try {
      await api.delete(`/artikel/${artId}`);
      zeigeMeldung('Artikel wurde gelöscht.');
      laden();
    } catch {
      zeigeMeldung('Artikel konnte nicht gelöscht werden. Bitte versuche es erneut.', 'error');
    }
  }

  // ── Schnelle Mengenänderung (debounced, race-safe) ───────────────────────────
  function mengePlusMinus(a, delta) {
    const aktuell = mengePendingRef.current[a.id] !== undefined ? mengePendingRef.current[a.id] : parseFloat(a.menge);
    const neu = Math.max(0, aktuell + delta);
    mengePendingRef.current[a.id] = neu;
    setArtikel(prev => prev.map(x => x.id === a.id ? { ...x, menge: neu } : x));
    clearTimeout(mengeTimerRef.current[a.id]);
    mengeTimerRef.current[a.id] = setTimeout(async () => {
      const ziel = mengePendingRef.current[a.id];
      delete mengePendingRef.current[a.id];
      try {
        await api.put(`/artikel/${a.id}`, {
          name: a.name, beschreibung: a.beschreibung || '', barcode: a.barcode || '',
          menge: ziel, einheit: a.einheit, kategorie: a.kategorie || '', notizen: a.notizen || '',
        });
      } catch {
        zeigeMeldung('Menge konnte nicht geändert werden.', 'error');
        laden();
      }
    }, 600);
  }

  // ── Barcode-/QR-Scanner ───────────────────────────────
  async function barcodeScanner() {
    await scannerStarten('Barcode scannen', async (code) => {
      try {
        const r = await api.get(`/barcode/${encodeURIComponent(code)}`);
        setForm((f) => ({ ...f, barcode: code, name: r.data.name || f.name }));
      } catch {
        setForm((f) => ({ ...f, barcode: code }));
      }
      setZeigForm(true);
    });
  }

  async function qrCodeScannenUndZuordnen() {
    await scannerStarten('QR-Code zuordnen', async (code) => {
      try {
        const r = await api.post(`/behaelter/${id}/qr/assign`, { code });
        setBehaelter(r.data);
        zeigeMeldung('QR-Code wurde erfolgreich zugeordnet.');
      } catch (error) {
        setScanner({ status: scannerStatuses.error, message: error.response?.data?.error || 'QR-Code konnte nicht zugeordnet werden.', reason: 'assign' });
      }
    });
  }

  async function scannerStarten(title, onCode) {
    if (isScannerBusy(scanner) || scannerRef.current) return;
    setScanner({ status: scannerStatuses.starting, message: 'Kamera wird gestartet...', reason: '' });
    const controller = await startBarcodeScanner({
      title,
      onStateChange: (nextState) => setScanner((current) => ({ ...current, ...nextState })),
      onClose: () => {
        scannerRef.current = null;
        setScanner({ status: scannerStatuses.idle, message: '', reason: '' });
      },
      onError: (error) => {
        if (!error.overlay) scannerRef.current = null;
        setScanner({ status: scannerStatuses.error, message: error.message, reason: error.reason });
      },
      onResult: async (code) => {
        scannerRef.current = null;
        setScanner({ status: scannerStatuses.idle, message: '', reason: '' });
        await onCode(code);
      },
    });
    scannerRef.current = controller;
  }

  // ── QR-Code ───────────────────────────────────────────
  async function qrCodeErstellen() {
    try {
      const r = await api.post(`/behaelter/${id}/qr/generate`);
      setBehaelter(r.data);
      zeigeMeldung('QR-Code wurde erstellt. Du kannst ihn jetzt ausdrucken.');
    } catch (error) {
      zeigeMeldung(error.response?.data?.error || 'QR-Code konnte nicht erstellt werden.', 'error');
    }
  }

  async function qrCodeEntfernen() {
    if (!window.confirm(`QR-Code-Zuordnung von Kiste „${behaelter?.name}" wirklich entfernen?\n\nGedruckte Codes öffnen danach nicht mehr diese Kiste.`)) return;
    try {
      await api.delete(`/behaelter/${id}/qr`);
      zeigeMeldung('QR-Code-Zuordnung wurde entfernt.');
      laden();
    } catch {
      zeigeMeldung('QR-Code konnte nicht entfernt werden.', 'error');
    }
  }

  function qrCodeDrucken() {
    window.open(`/behaelter/${id}/etikett`, '_blank');
  }

  // ── Fotos ─────────────────────────────────────────────
  async function fotoHochladen(e, typ, itemId) {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('foto', file);
    try {
      await api.post(`/${typ}/${itemId}/foto`, fd);
      laden();
      zeigeMeldung('Foto wurde hochgeladen.');
    } catch {
      zeigeMeldung('Foto konnte nicht hochgeladen werden.', 'error');
    }
  }

  function artikelFotoClick(artId) {
    setUploadArtikelId(artId);
    artikelFotoRef.current.value = '';
    artikelFotoRef.current.click();
  }

  async function artikelFotoHochladen(e) {
    if (!e.target.files[0] || !uploadArtikelId) return;
    await fotoHochladen(e, 'artikel', uploadArtikelId);
    setUploadArtikelId(null);
  }

  // ── Formular-Hilfsfunktionen ──────────────────────────
  function bearbeiten(a) {
    setForm({ name: a.name, beschreibung: a.beschreibung||'', barcode: a.barcode||'', menge: a.menge||'1', einheit: a.einheit||'Stück', kategorie: a.kategorie||'', notizen: a.notizen||'' });
    setBearbeite(a.id);
    setFormFehler('');
    setZeigForm(true);
  }

  function formSchliessen() {
    setZeigForm(false);
    setBearbeite(null);
    setFormFehler('');
    setForm({ name: '', beschreibung: '', barcode: '', menge: '1', einheit: 'Stück', kategorie: '', notizen: '' });
  }

  const inp = { width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', marginBottom: '10px' };
  const scannerHint = getScannerPageHint(scanner);
  const scannerBusy = isScannerBusy(scanner);

  if (!behaelter) return (
    <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
      <div style={{ fontSize: '40px' }}>⏳</div>
      <div style={{ marginTop: '12px' }}>Kiste wird geladen...</div>
    </div>
  );

  return (
    <div>
      {/* ── Header ────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <button onClick={() => navigate('/behaelter')} aria-label="Zurück zur Kistenübersicht"
          style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '4px 8px' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>{behaelter.name}</h1>
          {behaelter.standort_name && <div style={{ color: '#64748b', fontSize: '13px' }}>{behaelter.standort_icon} {behaelter.standort_name}</div>}
        </div>
        <button onClick={kisteBearbeitenOeffnen} aria-label="Kiste bearbeiten"
          title="Kiste bearbeiten"
          style={{ background: '#f1f5f9', border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', color: '#374151' }}>
          ✏️ <span>Bearbeiten</span>
        </button>
      </div>

      {behaelter.foto_url && <img src={behaelter.foto_url} alt={`Foto: ${behaelter.name}`} style={{ width: '100%', borderRadius: '12px', marginBottom: '12px', maxHeight: '200px', objectFit: 'cover' }} />}

      {/* ── Meldungen ─────────────────────────────────── */}
      {meldung && (
        <div role={meldungTyp === 'error' ? 'alert' : 'status'}
          style={{ background: meldungTyp === 'error' ? '#fff7ed' : '#f0fdf4', color: meldungTyp === 'error' ? '#9a3412' : '#166534', border: `1px solid ${meldungTyp === 'error' ? '#fed7aa' : '#bbf7d0'}`, borderRadius: '12px', padding: '12px', marginBottom: '12px', fontSize: '13px' }}>
          {meldung}
        </div>
      )}
      {scanner.message && scanner.status === scannerStatuses.error && (
        <div role="alert" style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: '12px', padding: '12px', marginBottom: '12px', fontSize: '13px', lineHeight: 1.4 }}>
          {scanner.message}
          {scannerHint && <div style={{ marginTop: '8px', color: '#64748b' }}>{scannerHint}</div>}
        </div>
      )}

      {/* ── QR-Code & Etikett ─────────────────────────── */}
      <section aria-label="QR-Code & Etikett" style={{ background: 'white', borderRadius: '14px', padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '16px' }}>QR-Code & Etikett</h2>
        {behaelter.qr_code ? (
          <div>
            <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 12px', lineHeight: '1.4' }}>
              Diesen QR-Code auf die Kiste kleben. Beim Scannen öffnet sich direkt diese Kiste.
            </p>
            <img src={behaelter.qr_code} alt={`QR-Code für ${behaelter.name}`} style={{ width: '140px', height: '140px', display: 'block', margin: '0 auto 12px', imageRendering: 'pixelated' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button onClick={qrCodeDrucken}
                style={{ gridColumn: '1 / -1', padding: '11px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '14px' }}>
                🖨️ Etikett drucken
              </button>
              <button onClick={qrCodeScannenUndZuordnen} disabled={scannerBusy}
                style={{ padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
                📷 Anderen QR scannen
              </button>
              <button onClick={qrCodeEntfernen}
                style={{ padding: '10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
                QR entfernen
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px' }}>
              Diese Kiste hat noch keinen QR-Code. Erstelle einen und drucke ihn aus, um den Behälter per Scan zu öffnen.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button onClick={qrCodeErstellen}
                style={{ padding: '10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}>
                ✨ QR-Code erstellen
              </button>
              <button onClick={qrCodeScannenUndZuordnen} disabled={scannerBusy}
                style={{ padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>
                📷 Vorhandenen QR scannen
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Artikel-Aktionsleiste ─────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => setZeigForm(true)}
          style={{ flex: 1, padding: '12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
          + Artikel hinzufügen
        </button>
        <button onClick={barcodeScanner} disabled={scannerBusy}
          title="Barcode scannen und Artikel anlegen"
          aria-label="Barcode scannen und Artikel anlegen"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 14px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: scannerBusy ? 'wait' : 'pointer', opacity: scannerBusy ? 0.65 : 1, gap: '2px' }}>
          <span style={{ fontSize: '20px' }}>📷</span>
          <span style={{ fontSize: '10px', fontWeight: '600', color: '#374151' }}>Barcode</span>
        </button>
        <button onClick={() => fotoRef.current.click()}
          title="Kistenfoto hochladen"
          aria-label="Kistenfoto hochladen"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 14px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: 'pointer', gap: '2px' }}>
          <span style={{ fontSize: '20px' }}>🖼️</span>
          <span style={{ fontSize: '10px', fontWeight: '600', color: '#374151' }}>Foto</span>
        </button>
        <input ref={fotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => fotoHochladen(e, 'behaelter', id)} />
        <input ref={artikelFotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={artikelFotoHochladen} />
      </div>

      {/* ── Artikelliste ─────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {artikel.map(a => (
          <div key={a.id} style={{ background: 'white', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              {a.foto_url && <img src={a.foto_url} alt={a.name} style={{ width: '56px', height: '56px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '700', fontSize: '15px' }}>{a.name}</div>
                {a.beschreibung && <div style={{ color: '#64748b', fontSize: '12px' }}>{a.beschreibung}</div>}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Schnelle Mengenänderung */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: '#f8fafc', borderRadius: '8px' }}>
                    <button onClick={() => mengePlusMinus(a, -1)}
                      aria-label={`Menge von ${a.name} verringern`}
                      style={{ background: 'none', border: 'none', cursor: parseFloat(a.menge) <= 0 ? 'not-allowed' : 'pointer', fontSize: '16px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: parseFloat(a.menge) <= 0 ? '#cbd5e1' : '#475569', borderRadius: '6px', flexShrink: 0 }}>−</button>
                    <span style={{ fontSize: '12px', fontWeight: '600', minWidth: '36px', textAlign: 'center', whiteSpace: 'nowrap' }}>{parseFloat(a.menge) % 1 === 0 ? parseInt(a.menge) : a.menge} {a.einheit}</span>
                    <button onClick={() => mengePlusMinus(a, 1)}
                      aria-label={`Menge von ${a.name} erhöhen`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', borderRadius: '6px', flexShrink: 0 }}>+</button>
                  </div>
                  {a.kategorie && <span style={{ background: '#eef2ff', color: '#6366f1', padding: '2px 8px', borderRadius: '99px', fontSize: '11px' }}>{a.kategorie}</span>}
                  {a.barcode && <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '2px 8px', borderRadius: '99px', fontSize: '11px' }}>🏷️ {a.barcode}</span>}
                </div>
                {a.notizen && <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>📝 {a.notizen}</div>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <button onClick={() => bearbeiten(a)} aria-label={`${a.name} bearbeiten`} title="Artikel bearbeiten"
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', lineHeight: 1 }}>✏️</button>
                <button onClick={() => artikelFotoClick(a.id)} aria-label={`Foto für ${a.name} hochladen`} title="Artikelfoto hochladen"
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', lineHeight: 1 }}>🖼️</button>
                <button onClick={() => loeschen(a.id, a.name)} aria-label={`${a.name} löschen`} title="Artikel löschen"
                  style={{ background: '#fee2e2', border: 'none', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', color: '#ef4444', lineHeight: 1 }}>🗑️</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {artikel.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: '48px' }}>📭</div>
          <div style={{ marginTop: '12px', fontWeight: '600', fontSize: '15px', color: '#64748b' }}>Noch keine Artikel in dieser Kiste</div>
          <div style={{ fontSize: '13px', marginTop: '6px', marginBottom: '16px' }}>Füge den ersten Artikel hinzu.</div>
          <button onClick={() => setZeigForm(true)}
            style={{ padding: '10px 20px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700' }}>
            + Artikel hinzufügen
          </button>
        </div>
      )}

      {/* ── Artikel-Formular ──────────────────────────── */}
      {zeigForm && (
        <div role="dialog" aria-label={bearbeite ? 'Artikel bearbeiten' : 'Artikel hinzufügen'}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '24px', width: '100%', maxWidth: '600px', margin: '0 auto', maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px' }}>{bearbeite ? '✏️ Artikel bearbeiten' : '➕ Artikel hinzufügen'}</h2>
            {formFehler && <div role="alert" style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px' }}>{formFehler}</div>}
            <form onSubmit={speichern}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Artikelname <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input style={inp} placeholder="z.B. Kaffeefilter, Schrauben M8" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required autoFocus />

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Beschreibung <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span>
              </label>
              <input style={inp} placeholder="Zusätzliche Details" value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} />

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Barcode <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span>
              </label>
              <input style={inp} placeholder="Barcode-Nummer" value={form.barcode} onChange={e => setForm({...form, barcode: e.target.value})} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Menge</label>
                  <input style={{ ...inp, marginBottom: 0 }} type="number" step="0.1" min="0" placeholder="1" value={form.menge} onChange={e => setForm({...form, menge: e.target.value})} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Einheit</label>
                  <select style={{ ...inp, marginBottom: 0 }} value={form.einheit} onChange={e => setForm({...form, einheit: e.target.value})}>
                    {['Stück','kg','g','l','ml','Packung','Box','Rolle','Meter'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px', marginTop: '10px' }}>
                Kategorie <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span>
              </label>
              <input style={inp} placeholder="z.B. Werkzeug, Deko, Lebensmittel" value={form.kategorie} onChange={e => setForm({...form, kategorie: e.target.value})} />

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Notizen <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span>
              </label>
              <input style={inp} placeholder="Interne Hinweise" value={form.notizen} onChange={e => setForm({...form, notizen: e.target.value})} />

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button type="button" onClick={formSchliessen}
                  style={{ flex: 1, padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  Abbrechen
                </button>
                <button type="submit" disabled={speichert}
                  style={{ flex: 2, padding: '12px', background: speichert ? '#d97706' : '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', cursor: speichert ? 'wait' : 'pointer', fontWeight: '700', opacity: speichert ? 0.85 : 1 }}>
                  {speichert ? '⏳ Wird gespeichert...' : (bearbeite ? '💾 Änderungen speichern' : '✅ Artikel hinzufügen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Kiste bearbeiten ──────────────────────────── */}
      {zeigKisteForm && (
        <div role="dialog" aria-label="Kiste bearbeiten"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '24px', width: '100%', maxWidth: '600px', margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px' }}>✏️ Kiste bearbeiten</h2>
            {kisteFehler && <div role="alert" style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px' }}>{kisteFehler}</div>}
            <form onSubmit={kisteSpeichern}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Name der Kiste <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input style={inp} placeholder="z.B. Wohnzimmer-Box" value={kisteForm.name} onChange={e => setKisteForm({...kisteForm, name: e.target.value})} required autoFocus />

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                Beschreibung <span style={{ color: '#94a3b8', fontWeight: '400' }}>(optional)</span>
              </label>
              <input style={inp} placeholder="Wofür wird diese Kiste verwendet?" value={kisteForm.beschreibung} onChange={e => setKisteForm({...kisteForm, beschreibung: e.target.value})} />

              {standorte.length > 0 && (
                <>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Standort</label>
                  <select style={inp} value={kisteForm.standort_id} onChange={e => setKisteForm({...kisteForm, standort_id: e.target.value})}>
                    <option value="">– Kein Standort –</option>
                    {standorte.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}
                  </select>
                </>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>Farbe</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {FARBEN.map(f => (
                    <button key={f} type="button" onClick={() => setKisteForm({...kisteForm, farbe: f})}
                      aria-label={`Farbe ${f} auswählen`}
                      style={{ width: '32px', height: '32px', borderRadius: '50%', background: f, border: `3px solid ${kisteForm.farbe === f ? '#1e293b' : 'transparent'}`, cursor: 'pointer' }} />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setZeigKisteForm(false)}
                  style={{ flex: 1, padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  Abbrechen
                </button>
                <button type="submit" disabled={kisteSpeichert}
                  style={{ flex: 2, padding: '12px', background: kisteSpeichert ? '#d97706' : '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', cursor: kisteSpeichert ? 'wait' : 'pointer', fontWeight: '700', opacity: kisteSpeichert ? 0.85 : 1 }}>
                  {kisteSpeichert ? '⏳ Wird gespeichert...' : '💾 Änderungen speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
